import Locker from './LearningLocker';

/**
 * @typedef LearningLockerStorageConfig
 * @type {object}
 * @property {string} url
 * @property {string} user
 * @property {string} pass
 */

/**
 * @typedef LearningLockerPersonaAttribute
 * @type {object}
 * @property {string} _id
 * @property {string} key
 * @property {string} value
 * @property {string} original
 * @property {string} updatedAt
 * @property {string} createdAt
 * @property {boolean} recentlyCreated
 * @property {boolean} changed
 */

let INSTANCED = false;

/**
 * @param {LearningLockerPersonaAttribute} data
 * @return {LearningLockerPersonaAttribute}
 */
const toMeta = data => ({
    _id: data._id,
    key: data.key,
    value: data.value,
    original: data.value,
    updatedAt: data.updatedAt ?? undefined,
    createdAt: data.createdAt ?? undefined,
    get recentlyCreated() {
        return this.updatedAt === undefined
            || this.createdAt === undefined;
    },
    get changed() {
        return this.value !== this.original;
    },
});

/**
 * @class LearningLockerStorage
 */
const LearningLockerStorage = Component.extend({
    initialize(config) {
        Logger.info(
            '%cLearningLockerStorage :: initialize',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            { config }
        );
        if (!config) {
            throw new Error('Firebase config is required');
        }

        if (INSTANCED) {
            throw new Error(
                'You cannot start multiple instances of LRS Storage. Check that container components does not duplicate LrsStorage',
            );
        }

        // Prevent multiple instances
        INSTANCED = true;

        this.name = 'LearningLockerStorage';
        /** @type {LearningLockerStorageConfig} */
        this.config = config;
        /** @type {LearningLocker} */
        this.locker = new Locker(this.config);
        /**
         * @private
         * @type {Object<string, LearningLockerPersonaAttribute>}
         */
        this._data = {};
    },

    setup() {
        Logger.info(
            '%cLearningLockerStorage :: setup',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            {}
        );
    },

    /**
     * Load data for the state based on the email provided
     * @param {string} email - User's raw email address
     * @param {string[]} filter - Only return these attributes
     * @return {Object<string, any>} - Object of key/value pairs
     */
    async load(email, filter = []) {
        Logger.info(
            '%cLearningLockerStorage :: load',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            { email, filter }
        );
        const personaId = await this.locker.findOrCreatePersonaId(email, email);

        Logger.info(
            '%cLearningLockerStorage.load :: personaId',
            'background: lightgrey; color: crimson; padding: 0.2em 1em;',
            { personaId },
        );

        if (!personaId) {
            return {};
        }

        // The collection of attributes to be returned later
        // Some values may be overwritten as newer values are returned from
        // the requests (some personas have duplicated attributes keys and with
        // the responses being oldest to newest, the most recent value will be
        // used)

        const edges = await this.locker.getPersonaAttributeNodes(personaId,
            filter);

        const entries = edges.map(edge => ([edge.key, toMeta(edge)]));

        this._data = Object.fromEntries(entries);

        return this._data;
    },

    /**
     * @param {string} emailKey - The key for the attribute that is used to
     * reference this user
     * @param {LearningLockerPersonaAttribute[]} data
     * @returns {boolean}
     */
    async save(emailKey = 'email') {
        Logger.info(
            '%cLearningLockerStorage :: save',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            { emailKey }
        );

        /** @type {LearningLockerPersonaAttribute[]} */
        const data = Object.values(this._data);
        const personaId = await this.locker.findOrCreatePersonaId(
            this._data[emailKey].original,
            [
                this._data.firstName.original,
                this._data.lastName.original,
            ].join(' '),
        );

        if (!personaId) {
            return false;
        }

        if (this._data.email.changed) {
            // Email changing disabled for now
            // Revert email if the value is somehow changed
            // this._data.email.value = this._data.email.original;

            await this.locker.addPersonaIdentifier(
                personaId,
                this._data.email.value,
            );

            const oldPersonaIdentifier = await this.locker.getPersonaIdentifier(this._data.email.original);
            const oldPersonaIdentifierId = oldPersonaIdentifier?.data?.[0]?._id;

            if (oldPersonaIdentifierId) {
                await this.locker.deletePersonaIdentifier(oldPersonaIdentifierId)
            } else {
                console.warn(`Could not remove old persona identifier: '${this._data.email.original}'`)
            }

            Logger.info({
                data: JSON.parse(JSON.stringify(this._data)),
                oldPersonaIdentifier,
                oldPersonaIdentifierId,
            })
        }

        for (const attribute of data) {
            const { _id, value, key, changed, recentlyCreated } = attribute;
            Logger.info(
                '%cLL Attr',
                'background: lightgrey; color: purple; padding: 0.2em 1em;',
                { changed, _id, value, key, recentlyCreated }
            );

            if (!changed && !recentlyCreated) {
                continue;
            }

            let response = null;

            if (_id) {
                response = await this.locker.updateAttribute(_id, value);
            } else {
                response = await this.locker.addAttribute(personaId,
                    key,
                    value);
            }

            // Update the
            this._data[key] = toMeta(response.data);
        }

        return this._data;
    },

    clear() {
        Logger.info(
            '%cLearningLockerStorage :: clear',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            {}
        );
        this._data = {};
        return true;
    },

    data() {
        Logger.info(
            '%cLearningLockerStorage :: data',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            {}
        );
        return this._data;
    },

    /**
     * Convert the learning locker persona attributes array to a key/value
     * object
     * @return {Object<string, any>}
     */
    asObject() {
        Logger.info(
            '%cLearningLockerStorage :: asObject',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            {}
        );
        if (!this._data) {
            return undefined;
        }

        const entries = Object.values(this._data)
            .map(({ key, value }) => ([key, value]));

        return Object.fromEntries(entries);
    },

    /**
     * Convert a key/value object to the array of objects that is required for
     * this storage method
     * @param {Object<string, any>} dataObject
     * @return {LearningLockerPersonaAttribute}
     */
    setObject(dataObject) {
        Logger.info(
            '%cLearningLockerStorage :: setObject',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            { dataObject }
        );
        const entries = Object.entries(dataObject);

        for (const [key, value] of entries) {
            this.setAttribute(key, value);
        }
    },

    setAttribute(key, value) {
        const attribute = this._data[key];
        Logger.info(
            '%cLearningLockerStorage :: setAttribute',
            'background: lightgrey; color: red; padding: 0.2em 1em;',
            { key, value, attribute }
        );

        if (!attribute) {
            this._data[key] = toMeta({ key, value, recentlyCreated: true });
            return;
        }

        attribute.value = value;

        if (attribute.recentlyCreated) {
            attribute.original = value;
        }
    },
});

window.LearningLockerStorage = LearningLockerStorage;
